import type { IJsonModel, IJsonBorderNode } from 'flexlayout-react';
import type { SidebarPosition } from '../stores/uiStore';

/**
 * Default sizes for each dock edge. Left/right are wider (vertical panels);
 * top/bottom are shorter (horizontal strips).
 */
const BORDER_SIZES: Record<IJsonBorderNode['location'], number> = {
  left: 250,
  right: 250,
  top: 200,
  bottom: 200,
};

const DOCK_LOCATIONS: IJsonBorderNode['location'][] = ['top', 'left', 'right', 'bottom'];

/**
 * Guarantee a border exists on every edge (top/left/right/bottom) so ANY panel
 * can be dragged onto ANY side and dock there as a rotated tab — WebStorm-style.
 * Existing borders (with whatever panels the user has docked) are preserved;
 * missing edges are filled in as empty strips. Empty borders stay visible as
 * thin dock strips (FlexLayout's `enableAutoHide` defaults to off), which is the
 * drop affordance.
 */
export function ensureDockBorders(existing: IJsonBorderNode[] = []): IJsonBorderNode[] {
  const byLocation = new Map(existing.map((b) => [b.location, b]));
  return DOCK_LOCATIONS.map(
    (loc) =>
      byLocation.get(loc) ?? {
        type: 'border',
        location: loc,
        size: BORDER_SIZES[loc],
        children: [],
      },
  );
}

/**
 * Default layout for first-time users:
 *
 * ┌───────────────── top (dock) ─────────────────┐
 * │Projects │ Terminal | Oview  │  Files (40%)│ R │
 * │(border) │     (60%)        │             │ i │
 * │         │                  │             │ g │
 * └──────────────── bottom (dock) ───────────────┘
 *
 * All four edges are dock strips: drag any panel to an edge to dock it there.
 */
export function getDefaultLayout(position: SidebarPosition = 'left'): IJsonModel {
  const projectsBorder: IJsonBorderNode = {
    type: 'border',
    location: position,
    size: 250,
    children: [
      {
        type: 'tab',
        id: 'projects-border',
        name: 'Projects',
        component: 'projects',
        enableClose: false,
      },
    ],
  };

  return {
    global: {
      splitterSize: 4,
      tabEnableRename: false,
      // --- Tab drag/drop: let users regroup panels freely ---
      // Tabs can be picked up and dragged...
      tabEnableDrag: true,
      // ...dropped onto any other tabset to group them together...
      tabSetEnableDrop: true,
      // ...the tabset they're dragged out of can be dragged whole...
      tabSetEnableDrag: true,
      // ...and dropping near a tabset edge splits it into a new tabset.
      tabSetEnableDivide: true,
      // Docking against the outer edges of the layout creates new rows/cols.
      enableEdgeDock: true,
      tabDragSpeed: 0.15,
      tabSetEnableMaximize: true,
      tabSetEnableClose: false,
      borderSize: 250,
      // Keep empty borders visible as thin dock strips so every edge is always
      // a valid drop target (not just the ones that currently hold a panel).
      borderEnableAutoHide: false,
    },
    // All four edges are dock targets; Projects starts on `position`.
    borders: ensureDockBorders([projectsBorder]),
    layout: {
      type: 'row',
      weight: 100,
      children: [
        {
          type: 'tabset',
          weight: 60,
          children: [
            {
              type: 'tab',
              id: 'terminal-main',
              name: 'Terminal',
              component: 'terminal',
              enableClose: false,
            },
          ],
        },
        {
          type: 'tabset',
          weight: 40,
          children: [
            {
              type: 'tab',
              id: 'files-main',
              name: 'Files',
              component: 'files',
              enableClose: true,
            },
          ],
        },
      ],
    },
  };
}

export const DEFAULT_LAYOUT: IJsonModel = getDefaultLayout('left');
